import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store/store';
import { fetchSales } from '../../features/sales/salesSlice';
import { fetchVendors } from '../../features/vendors/vendorSlice';
import { fetchCrops } from '../../features/crops/cropSlice';
import SaleCard from './SaleCard';
import AddSaleForm from './AddSaleForm';
import SalesFilters from './SalesFilters';
import SalesSummary from './SalesSummary';
import SalesAnalytics from './analytics/SalesAnalytics';
import Button from '../ui/Button';
import { BarChart2 } from 'react-feather';
import LoadingSpinner from '../ui/LoadingSpinner';
import ErrorMessage from '../ui/ErrorMessage';

const SalesList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { sales, loading, error } = useSelector((state: RootState) => state.sales);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    minAmount: '',
    maxAmount: '',
    vendorId: ''
  });

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        dispatch(fetchSales()),
        dispatch(fetchVendors()),
        dispatch(fetchCrops())
      ]);
    };
    loadData();
  }, [dispatch]);

  const handleFilterChange = (name: string, value: string) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const filteredSales = sales.filter(sale => {
    const matchesDateRange = (!filters.startDate || new Date(sale.sale_date) >= new Date(filters.startDate)) &&
      (!filters.endDate || new Date(sale.sale_date) <= new Date(filters.endDate));
    
    const matchesAmount = (!filters.minAmount || sale.total_amount >= Number(filters.minAmount)) &&
      (!filters.maxAmount || sale.total_amount <= Number(filters.maxAmount));
    
    const matchesVendor = !filters.vendorId || sale.vendor_id === filters.vendorId;
    
    return matchesDateRange && matchesAmount && matchesVendor;
  });

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-2xl font-bold text-gray-900">{t('sales.title')}</h2>
        <div className="flex flex-wrap gap-6">
          <Button
            variant="secondary"
            onClick={() => setShowAnalytics(!showAnalytics)}
            className="flex items-center gap-2"
          >
            <BarChart2 className="w-4 h-4" />
            {showAnalytics ? t('common.close') : t('sales.analytics')}
          </Button>
          <Button 
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-3"
          >
            {showAddForm ? t('common.cancel') : t('sales.addSale')}
          </Button>
        </div>
      </div>

      {showAddForm && (
        <AddSaleForm onSuccess={() => setShowAddForm(false)} />
      )}

      {showAnalytics && <SalesAnalytics />}

      <SalesFilters
        filters={filters}
        onFilterChange={handleFilterChange}
      />

      <SalesSummary sales={filteredSales} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredSales.length === 0 ? (
          <p className="text-gray-500 col-span-full text-center py-8">
            {t('sales.noSales')}
          </p>
        ) : (
          filteredSales.map((sale) => (
            <SaleCard key={sale.id} sale={sale} />
          ))
        )}
      </div>
    </div>
  );
};

export default SalesList;